import type { GatsbyBrowser } from "gatsby"

// Ensure privacy-preferences link placeholders exist before external scripts
// (OneTrust / GTM) try to bind to them. The real elements are rendered by the
// Footer component; these placeholders are removed once React hydrates.
if (typeof document !== "undefined") {
	for (const id of ["display-pref-center", "display-pref-center-mobile"]) {
		if (!document.getElementById(id)) {
			const el = document.createElement("a")
			el.id = id
			el.hidden = true
			document.body.appendChild(el)
		}
	}
}

const PAGE_CLASS_PREFIX = "page--"
let hasBoundJumpLinkScrollHandler = false

const getTopScrollOffset = () => {
	const selectors = ["header", ".main-nav", ".top-nav", '[data-scroll-offset="true"]']
	const candidates = selectors.flatMap(selector =>
		Array.from(document.querySelectorAll<HTMLElement>(selector))
	)

	const topOverlayBottom = candidates.reduce((maxBottom, element) => {
		const styles = globalThis.getComputedStyle(element)
		const isTopOverlay =
			(styles.position === "fixed" || styles.position === "sticky") &&
			element.getBoundingClientRect().bottom > 0 &&
			element.getBoundingClientRect().top <= 1

		if (!isTopOverlay) {
			return maxBottom
		}

		return Math.max(maxBottom, element.getBoundingClientRect().bottom)
	}, 0)

	return Math.ceil(topOverlayBottom)
}

const isSamePageHashLink = (anchor: HTMLAnchorElement) => {
	if (!anchor.hash) {
		return false
	}

	const url = new URL(anchor.href, globalThis.location.href)
	return url.pathname === globalThis.location.pathname && url.hash.length > 1
}

const smoothScrollToAnchorFromJumpLink = (event: MouseEvent) => {
	if (event.defaultPrevented) {
		return
	}

	const eventTarget = event.target
	if (!(eventTarget instanceof Element)) {
		return
	}

	const anchor = eventTarget.closest(".jump-links a")
	if (!(anchor instanceof HTMLAnchorElement) || !isSamePageHashLink(anchor)) {
		return
	}

	const targetId = decodeURIComponent(anchor.hash.slice(1))
	const targetElement = document.getElementById(targetId)
	if (!targetElement) {
		return
	}

	event.preventDefault()
	const offset = getTopScrollOffset()
	const targetTop =
		targetElement.getBoundingClientRect().top + globalThis.scrollY - offset - 8
	globalThis.scrollTo({
		top: Math.max(targetTop, 0),
		behavior: "smooth",
	})

	history.pushState(null, "", `#${encodeURIComponent(targetId)}`)
}

const bindJumpLinksSmoothScroll = () => {
	if (typeof document === "undefined" || hasBoundJumpLinkScrollHandler) {
		return
	}

	document.addEventListener("click", smoothScrollToAnchorFromJumpLink)
	hasBoundJumpLinkScrollHandler = true
}

let hasBoundSkipLinkHandler = false

const getStickyHeaderHeight = (): number => {
	const header = document.querySelector<HTMLElement>("header")
	if (!header) {
		return 0
	}
	const styles = globalThis.getComputedStyle(header)
	if (styles.position !== "fixed" && styles.position !== "sticky") {
		return 0
	}
	return Math.ceil(header.getBoundingClientRect().height)
}

const handleSkipLinkClick = (event: MouseEvent) => {
	if (event.defaultPrevented) {
		return
	}
	const eventTarget = event.target
	if (!(eventTarget instanceof Element)) {
		return
	}
	const link = eventTarget.closest<HTMLAnchorElement>(".skip-to-main")
	if (!link) {
		return
	}

	const main = document.getElementById("main-content")
	if (!main) {
		return
	}

	event.preventDefault()

	// Move keyboard/AT focus into <main> (it has tabindex=-1) without the
	// browser's anchor-jump using global scroll-padding-top.
	main.focus({ preventScroll: true })

	const headerOffset = getStickyHeaderHeight()
	const targetTop = main.getBoundingClientRect().top + globalThis.scrollY - headerOffset
	const prefersReducedMotion = globalThis.matchMedia?.(
		"(prefers-reduced-motion: reduce)"
	).matches
	globalThis.scrollTo({
		top: Math.max(0, targetTop),
		behavior: prefersReducedMotion ? "auto" : "smooth",
	})
}

const bindSkipLinkHandler = () => {
	if (hasBoundSkipLinkHandler) {
		return
	}
	document.addEventListener("click", handleSkipLinkClick)
	hasBoundSkipLinkHandler = true
}

// ============================================================================
// Cross-Page Hash Scroll Fix
// ============================================================================

const scrollToHashAnchor = () => {
	if (typeof document === "undefined" || !globalThis.location.hash) {
		return
	}

	const targetId = decodeURIComponent(globalThis.location.hash.slice(1))
	if (!targetId) {
		return
	}

	const targetElement = document.getElementById(targetId)
	if (!(targetElement instanceof HTMLElement)) {
		return
	}

	const offset = getTopScrollOffset()
	const targetTop =
		targetElement.getBoundingClientRect().top + globalThis.scrollY - offset - 8

	globalThis.scrollTo({
		top: Math.max(targetTop, 0),
		behavior: "auto",
	})
}

const getPageClassName = (pathname: string) => {
	const normalizedPath = pathname.replace(/\/+$/, "") || "/"

	if (normalizedPath === "/") {
		return `${PAGE_CLASS_PREFIX}home`
	}

	const slug = normalizedPath
		.split("/")
		.filter(Boolean)
		.map(segment =>
			segment
				.toLowerCase()
				.replace(/[^a-z0-9-]/g, "-")
				.replace(/-+/g, "-")
				.replace(/^-|-$/g, "")
		)
		.filter(Boolean)
		.join("--")

	return `${PAGE_CLASS_PREFIX}${slug}`
}

const applyPageClassToBody = (pathname: string) => {
	if (typeof document === "undefined") {
		return
	}

	const nextClassName = getPageClassName(pathname)
	const pageClasses = Array.from(document.body.classList).filter(className =>
		className.startsWith(PAGE_CLASS_PREFIX)
	)

	if (pageClasses.length > 0) {
		document.body.classList.remove(...pageClasses)
	}

	document.body.classList.add(nextClassName)
	document.body.dataset.pageClassName = nextClassName
}

const applyPlatformClassToHtml = () => {
	if (typeof document === "undefined") {
		return
	}

	const html = document.documentElement
	const isMacPlatform =
		navigator.platform.toLowerCase().includes("mac") ||
		navigator.userAgent.toLowerCase().includes("mac os")

	html.classList.toggle("platform-mac", isMacPlatform)
}

export const onInitialClientRender: GatsbyBrowser["onInitialClientRender"] = () => {
	applyPageClassToBody(globalThis.location.pathname)
	applyPlatformClassToHtml()
	bindJumpLinksSmoothScroll()
	bindSkipLinkHandler()
	scrollToHashAnchor()
}

export const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = ({ location }) => {
	applyPageClassToBody(location.pathname)

	// Reset keyboard focus to the top of the document on client-side navigation
	// so the next Tab press lands on the "Skip to main content" link instead of
	// continuing from wherever the previously activated link was.
	// Skip focus reset when navigating to a hash, but still run hash scrolling below.
	if (!location.hash) {
		const skipLink = document.querySelector<HTMLAnchorElement>(".skip-to-main")
		if (skipLink) {
			// Defer until after @reach/router (used by Gatsby) auto-focuses the route
			// wrapper, otherwise our focus reset is immediately overridden.
			const resetFocusToTop = () => {
				const active = document.activeElement as HTMLElement | null
				if (active && active !== document.body && typeof active.blur === "function") {
					active.blur()
				}
				if (!document.body.hasAttribute("tabindex")) {
					document.body.setAttribute("tabindex", "-1")
				}
				try {
					document.body.focus({ preventScroll: true })
				} catch {
					document.body.focus()
				}
			}

			if (typeof globalThis.requestAnimationFrame === "function") {
				// Two RAFs ensures we run after reach-router's post-render focus call.
				globalThis.requestAnimationFrame(() => {
					globalThis.requestAnimationFrame(resetFocusToTop)
				})
			} else {
				globalThis.setTimeout(resetFocusToTop, 0)
			}
		}
	}
	globalThis.setTimeout(() => {
		if (location.hash) {
			scrollToHashAnchor()
		} else {
			// Scroll to top when navigating to a page without a hash
			globalThis.scrollTo({ top: 0, left: 0, behavior: "auto" })
		}
	}, 100)
}